import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CheckIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  InformationCircleIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { shipmentsService } from '../services/logistics/shipments';
import { shipmentCostingsService } from '../services/logistics/shipment-costings';
import { enhancedCostingService } from '../services/logistics/enhanced-costing-service';
import { fuelCostCalculator } from '../services/logistics/fuel-cost-calculator';
import { driversService } from '../services/logistics/drivers';
import { vehiclesService } from '../services/logistics/vehicles';
import { expensesService } from '../services/accounting/expenses';
import { journalEntriesService } from '../services/accounting/journal-entries';
import { chartOfAccountsService } from '../services/accounting/chart-of-accounts';
import { accountMappingService } from '../services/accounting/account-mapping';
import { userPreferencesService } from '../services/system/user-preferences';
import { calculateVATBreakdown } from '../utils/vat-calculations';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import VATToggle from '../components/ui/VATToggle';
import FuelImportModal from '../components/logistics/FuelImportModal';
import FuelDataSection from '../components/logistics/FuelDataSection';
import SeparatedFuelDataSection from '../components/logistics/SeparatedFuelDataSection';
import SeparatedFuelCostSection from '../components/logistics/SeparatedFuelCostSection';
import SimplifiedFuelCostSection from '../components/logistics/SimplifiedFuelCostSection';
import FuelAnalyticsModal from '../components/logistics/FuelAnalyticsModal';
import BulkFuelCostManager from '../components/logistics/BulkFuelCostManager';
import CostCell from '../components/logistics/CostCell';
import { autoFixUnknownVehicles } from '../utils/fix-unknown-vehicles';
import toast from 'react-hot-toast';
import { ensureEnglishNumerals, formatNumber } from '../utils/formatting';

// Safe number parser that handles Arabic numerals and invalid inputs
const toNumber = (v) => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const s = ensureEnglishNumerals(v).replace(/[^\d.-]/g, '');
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const ShipmentCosting = ({ hideMetrics = false } = {}) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [costings, setCostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);

  // Date range filter states
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    totalCost: 0,
    totalProfit: 0,
    shipmentCount: 0,
    totalFuelCost: 0,
    totalMaintenanceCost: 0,
    totalCommission: 0
  });
  const [showCostingModal, setShowCostingModal] = useState(false);
  const [costingData, setCostingData] = useState({
    fuel: '',
    fuelIncludesVAT: true,
    fuelBaseAmount: 0,
    fuelVATAmount: 0,
    maintenance: '',
    maintenanceIncludesVAT: true,
    maintenanceBaseAmount: 0,
    maintenanceVATAmount: 0,
    stops: [],
    otherCosts: [],
    commission: '', // Commission is VAT-exempt
    notes: '', // Add notes field
    costingStatus: 'not_added' // Default status: not_added, partial, completed
  });
  const [editedAfterPosting, setEditedAfterPosting] = useState(false);
  const [originalPostedData, setOriginalPostedData] = useState(null);

  // Add costing status filter
  const [costingStatusFilter, setCostingStatusFilter] = useState('all');

  // VAT preferences state
  const [vatPreferences, setVatPreferences] = useState({
    fuelVATDefault: true,
    maintenanceVATDefault: true
  });

  // Validation state for VAT calculations
  const [validationErrors, setValidationErrors] = useState({
    fuel: null,
    maintenance: null,
    commission: null,
    stops: [],
    otherCosts: []
  });

  // Fuel import state
  const [showFuelImportModal, setShowFuelImportModal] = useState(false);
  const [showFuelAnalyticsModal, setShowFuelAnalyticsModal] = useState(false);
  const [showBulkFuelManager, setShowBulkFuelManager] = useState(false);

  // Helper function to get date range
  const getDateRange = () => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    switch (dateRange) {
      case 'today':
        return { start: startOfToday, end: endOfToday };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const startOfYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        const endOfYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59);
        return { start: startOfYesterday, end: endOfYesterday };
      case '7days':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return { start: sevenDaysAgo, end: endOfToday };
      case 'month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: startOfMonth, end: endOfToday };
      case '6months':
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        return { start: sixMonthsAgo, end: endOfToday };
      case 'year':
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        return { start: startOfYear, end: endOfToday };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : startOfToday,
          end: customEndDate ? new Date(customEndDate + 'T23:59:59') : endOfToday
        };
      default:
        return { start: startOfToday, end: endOfToday };
    }
  };

  // Filter shipments by date range
  const getFilteredShipments = () => {
    const { start, end } = getDateRange();

    return shipments.filter(shipment => {
      // Use createdAt or deliveryDate for filtering
      const shipmentDate = shipment.deliveryDate ?
        new Date(shipment.deliveryDate) :
        new Date(shipment.createdAt?.seconds ? shipment.createdAt.seconds * 1000 : shipment.createdAt);

      return shipmentDate >= start && shipmentDate <= end;
    });
  };

  // Get costing for a specific shipment
  const getShipmentCosting = useCallback((shipmentId) => {
    return costings.find(costing => costing.shipmentId === shipmentId);
  }, [costings]);

  // Calculate total cost for a shipment using real-time fuel calculation
  const calculateTotalCost = useCallback(async (costing, shipmentId) => {
    if (!costing && !shipmentId) return 0;

    try {
      // Get real-time fuel costs if we have a shipment ID
      let fuelCost = 0;
      if (shipmentId && currentCompany?.id) {
        const fuelCosts = await fuelCostCalculator.calculateFuelCostsForShipment(
          currentCompany.id, 
          shipmentId
        );
        fuelCost = fuelCosts.totalFuelAmount;
      } else if (costing) {
        // Fallback to stored values if no shipment ID
        fuelCost = costing.totalFuelAmount || costing.fuel || 0;
      }

      // Other costs from stored costing data
      const maintenanceCost = costing?.maintenanceAmount || costing?.maintenance || 0;
      const stopsCost = (costing?.stops || []).reduce((sum, stop) => sum + (stop.amount || 0), 0);
      const otherCost = (costing?.otherCosts || []).reduce((sum, cost) => sum + (cost.amount || 0), 0);
      const commission = costing?.commissionAmount || costing?.commission || 0;

      return fuelCost + maintenanceCost + stopsCost + otherCost + commission;
    } catch (error) {
      console.error('Error calculating total cost:', error);
      // Fallback to stored values
      if (costing) {
        const fuelCost = costing.totalFuelAmount || costing.fuel || 0;
        const maintenanceCost = costing.maintenanceAmount || costing.maintenance || 0;
        const stopsCost = (costing.stops || []).reduce((sum, stop) => sum + (stop.amount || 0), 0);
        const otherCost = (costing.otherCosts || []).reduce((sum, cost) => sum + (cost.amount || 0), 0);
        const commission = costing.commissionAmount || costing.commission || 0;
        return fuelCost + maintenanceCost + stopsCost + otherCost + commission;
      }
      return 0;
    }
  }, [currentCompany?.id]);

  // Helper function to calculate total shipment revenue (excluding VAT)
  const calculateTotalShipmentPrice = useCallback((shipment) => {
    if (!shipment) return 0;

    const baseShipmentPrice = parseFloat(shipment.cost || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

    // Calculate base prices (excluding VAT) - VAT is not company revenue
    let mainBasePrice;
    if (shipment.vatIncluded) {
      // Price includes VAT - extract base price
      mainBasePrice = baseShipmentPrice / 1.15;
    } else {
      // Price excludes VAT - use as is
      mainBasePrice = baseShipmentPrice;
    }

    // For backhaul and itemized costs, assume they follow the same VAT setting as main price
    let backhaulBasePrice;
    if (shipment.vatIncluded) {
      backhaulBasePrice = backhaulPrice / 1.15;
    } else {
      backhaulBasePrice = backhaulPrice;
    }

    let itemizedBasePrice;
    if (shipment.vatIncluded) {
      itemizedBasePrice = itemizedTotal / 1.15;
    } else {
      itemizedBasePrice = itemizedTotal;
    }

    // Return only the base prices (company revenue) - VAT is excluded as it belongs to government
    const totalBasePrice = mainBasePrice + backhaulBasePrice + itemizedBasePrice;
    return totalBasePrice;
  }, []);

  // Calculate metrics for filtered shipments with real-time fuel costs
  const calculateMetrics = useCallback(async () => {
    const filteredShipments = getFilteredShipments();

    let totalRevenue = 0;
    let totalCost = 0;
    let totalFuelCost = 0;
    let totalMaintenanceCost = 0;
    let totalCommission = 0;
    let shipmentCount = filteredShipments.length;

    // Process shipments in parallel for better performance
    const promises = filteredShipments.map(async (shipment) => {
      try {
        // Calculate revenue (total shipment price excluding VAT)
        const revenue = calculateTotalShipmentPrice(shipment);

        // Get costing data
        const costing = getShipmentCosting(shipment.id);
        
        // Calculate real-time fuel cost
        let fuelCost = 0;
        if (currentCompany?.id) {
          try {
            const fuelCosts = await fuelCostCalculator.calculateFuelCostsForShipment(
              currentCompany.id, 
              shipment.id
            );
            fuelCost = fuelCosts.totalFuelAmount;
          } catch (error) {
            console.warn(`Failed to get real-time fuel cost for shipment ${shipment.id}, using stored value:`, error);
            // Fallback to stored values
            if (costing) {
              const petroAppFuel = parseFloat(costing.petroAppFuelAmount || 0);
              const manualFuel = parseFloat(costing.manualFuelAmount || 0);
              fuelCost = petroAppFuel + manualFuel;
            }
          }
        }

        // Other costs from stored costing data
        const maintenanceCost = costing ? parseFloat(costing.maintenanceAmount || costing.maintenance || 0) : 0;
        const commissionCost = costing ? parseFloat(costing.commissionAmount || costing.commission || 0) : 0;
        const stopsCost = costing ? (costing.stops || []).reduce((sum, stop) => sum + (stop.amount || 0), 0) : 0;
        const otherCost = costing ? (costing.otherCosts || []).reduce((sum, cost) => sum + (cost.amount || 0), 0) : 0;
        
        // Calculate total cost
        const cost = fuelCost + maintenanceCost + stopsCost + otherCost + commissionCost;

        return {
          revenue,
          cost,
          fuelCost,
          maintenanceCost,
          commissionCost
        };
      } catch (error) {
        console.error(`Error calculating metrics for shipment ${shipment.id}:`, error);
        return {
          revenue: 0,
          cost: 0,
          fuelCost: 0,
          maintenanceCost: 0,
          commissionCost: 0
        };
      }
    });

    // Wait for all calculations to complete
    const results = await Promise.all(promises);

    // Sum up all results
    results.forEach(result => {
      totalRevenue += result.revenue;
      totalCost += result.cost;
      totalFuelCost += result.fuelCost;
      totalMaintenanceCost += result.maintenanceCost;
      totalCommission += result.commissionCost;
    });

    const totalProfit = totalRevenue - totalCost;

    setMetrics({
      totalRevenue,
      totalCost,
      totalProfit,
      shipmentCount,
      totalFuelCost,
      totalMaintenanceCost,
      totalCommission
    });
  }, [shipments, costings, dateRange, customStartDate, customEndDate, getShipmentCosting, calculateTotalCost, calculateTotalShipmentPrice]);

  // Load shipments and costings
  const loadData = useCallback(async () => {
    if (!currentCompany?.id) return;

    try {
      setLoading(true);
      console.log('Loading data for company:', currentCompany.id);

      // Load shipments first
      let shipmentsData = [];
      try {
        shipmentsData = await shipmentsService.getShipments(currentCompany.id);
        console.log('Shipments loaded:', shipmentsData.length);

        // Enhance shipments with driver and vehicle info if missing
        const [drivers, vehicles] = await Promise.all([
          driversService.getDrivers(currentCompany.id),
          vehiclesService.getVehicles(currentCompany.id)
        ]);

        shipmentsData = shipmentsData.map(shipment => {
          console.log('Processing shipment:', shipment.trackingNumber, 'driverId:', shipment.driverId, 'vehicleId:', shipment.vehicleId);

          // If driver info is missing, try to find it
          if ((!shipment.driverName || shipment.driverName === 'Unknown Driver') && shipment.driverId) {
            const driver = drivers.find(d => d.id === shipment.driverId || d.id === parseInt(shipment.driverId) || d.id === shipment.driverId.toString());
            if (driver) {
              console.log('Found driver:', driver.name, 'for shipment:', shipment.trackingNumber);
              shipment.driverName = driver.name;
            } else {
              console.log('No driver found for ID:', shipment.driverId, 'Available drivers:', drivers.map(d => ({ id: d.id, name: d.name })));
            }
          }

          // If vehicle info is missing, try to find it and UPDATE the database
          if ((!shipment.vehiclePlate || shipment.vehiclePlate === 'Unknown Vehicle') && shipment.vehicleId) {
            const vehicle = vehicles.find(v => v.id === shipment.vehicleId || v.id === parseInt(shipment.vehicleId) || v.id === shipment.vehicleId.toString());
            if (vehicle) {
              console.log('Found vehicle:', vehicle.plateNumber, 'for shipment:', shipment.trackingNumber);
              shipment.vehiclePlate = vehicle.plateNumber;
              
              // Update the shipment in the database immediately
              shipmentsService.updateShipment(shipment.id, {
                vehiclePlate: vehicle.plateNumber
              }).catch(error => {
                console.error('Error updating shipment vehicle plate:', error);
              });
            } else {
              console.log('No vehicle found for ID:', shipment.vehicleId, 'Available vehicles:', vehicles.map(v => ({ id: v.id, plate: v.plateNumber })));
            }
          }

          return shipment;
        });

      } catch (shipmentError) {
        console.error('Error loading shipments:', shipmentError);
        toast.error('Failed to load shipments');
        return;
      }

      // Load costings separately to isolate the error
      let costingsData = [];
      try {
        costingsData = await shipmentCostingsService.getCostingsByCompany(currentCompany.id);
        console.log('Costings loaded:', costingsData.length);
      } catch (costingError) {
        console.error('Error loading costings:', costingError);
        // Don't fail completely if costings don't exist yet
        console.log('No costings found, this is normal for new installations');
        costingsData = [];
      }

      setShipments(shipmentsData);
      setCostings(costingsData);
      console.log('Data loading completed successfully');
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load shipment data');
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  // Load user VAT preferences
  const loadVATPreferences = useCallback(async () => {
    if (!user?.uid || !currentCompany?.id) return;

    try {
      const preferences = await userPreferencesService.getVATPreferences(user.uid, currentCompany.id);
      setVatPreferences(preferences);
    } catch (error) {
      console.error('Error loading VAT preferences:', error);
      // Use defaults on error
      setVatPreferences({
        fuelVATDefault: true,
        maintenanceVATDefault: true
      });
    }
  }, [user?.uid, currentCompany?.id]);

  useEffect(() => {
    loadData();
    loadVATPreferences();
  }, [loadData, loadVATPreferences]);

  // Recalculate metrics when data or date range changes
  useEffect(() => {
    if (shipments.length > 0) {
      calculateMetrics().catch(error => {
        console.error('Error calculating metrics:', error);
      });
    }
  }, [calculateMetrics]);



  // Check if costing is empty (auto-created but not edited)
  const isEmptyCosting = (costing) => {
    if (!costing) return true;
    return (
      costing.fuel === 0 &&
      costing.maintenance === 0 &&
      costing.commission === 0 &&
      (!costing.stops || costing.stops.length === 0) &&
      (!costing.otherCosts || costing.otherCosts.length === 0)
    );
  };



  // Helper function to calculate subtotal before tax
  const calculateSubtotalBeforeTax = (shipment) => {
    if (!shipment) return 0;

    const baseShipmentPrice = parseFloat(shipment.cost || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

    let mainBasePrice;

    if (shipment.isVatIncluded) {
      // Main price includes VAT, extract base price
      mainBasePrice = baseShipmentPrice / 1.15;
    } else {
      // All prices are without VAT
      mainBasePrice = baseShipmentPrice;
    }

    // Subtotal = base price + backhaul + itemized costs (all without VAT)
    const subtotal = mainBasePrice + backhaulPrice + itemizedTotal;

    return subtotal;
  };

  // Helper function to format date in DD/MM/YYYY format with English numerals
  const formatShipmentDate = (shipment) => {
    if (!shipment) return '';

    // Try to get the date from deliveryDate first, then createdAt
    let date;
    if (shipment.deliveryDate) {
      date = new Date(shipment.deliveryDate);
    } else if (shipment.createdAt) {
      date = shipment.createdAt?.seconds
        ? new Date(shipment.createdAt.seconds * 1000)
        : new Date(shipment.createdAt);
    } else {
      return '';
    }

    // Format as DD/MM/YYYY with English numerals
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // Helper function to format time in HH:MM format with English numerals
  const formatShipmentTime = (shipment) => {
    if (!shipment) return '';

    // Try to get the date from deliveryDate first, then createdAt
    let date;
    if (shipment.deliveryDate) {
      date = new Date(shipment.deliveryDate);
    } else if (shipment.createdAt) {
      date = shipment.createdAt?.seconds
        ? new Date(shipment.createdAt.seconds * 1000)
        : new Date(shipment.createdAt);
    } else {
      return '';
    }

    // Format as HH:MM with English numerals
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  };

  // Calculate VAT breakdown for fuel with enhanced validation
  const calculateFuelVAT = useCallback((amount, includesVAT) => {
    console.log('calculateFuelVAT called with:', { amount, includesVAT });
    
    // Handle edge cases
    if (!amount || amount <= 0 || isNaN(amount)) {
      console.log('calculateFuelVAT: returning zero values for invalid amount');
      return { baseAmount: 0, vatAmount: 0, total: 0, isValid: true };
    }
    
    // Validate amount is within reasonable bounds
    if (amount > 999999.99) {
      console.log('calculateFuelVAT: amount exceeds maximum');
      return { baseAmount: 0, vatAmount: 0, total: 0, isValid: false, error: 'Amount exceeds maximum allowed value' };
    }
    
    try {
      const result = calculateVATBreakdown(amount, includesVAT);
      console.log('calculateVATBreakdown result:', result);
      
      // More lenient validation with better error handling
      const tolerance = 0.05; // Increased tolerance for rounding differences
      
      if (includesVAT) {
        // For inclusive VAT, the result.total should equal the entered amount
        const difference = Math.abs(amount - result.total);
        console.log('VAT inclusive validation:', { amount, resultTotal: result.total, difference, tolerance });
        if (difference > tolerance) {
          console.warn('VAT calculation difference:', { amount, resultTotal: result.total, difference });
          // Still return the calculation but mark as potentially invalid
          return { 
            ...result, 
            isValid: true, // Allow it to proceed
            warning: `Minor calculation difference: ${difference.toFixed(3)}`
          };
        }
      } else {
        // For exclusive VAT, validate that base + VAT = total
        const calculatedTotal = result.baseAmount + result.vatAmount;
        const difference = Math.abs(result.total - calculatedTotal);
        console.log('VAT exclusive validation:', { resultTotal: result.total, calculatedTotal, difference, tolerance });
        if (difference > tolerance) {
          console.warn('VAT total calculation difference:', { 
            resultTotal: result.total, 
            calculatedTotal, 
            difference 
          });
          // Still return the calculation but mark as potentially invalid
          return { 
            ...result, 
            isValid: true, // Allow it to proceed
            warning: `Minor total calculation difference: ${difference.toFixed(3)}`
          };
        }
      }
      
      console.log('calculateFuelVAT: returning valid result:', { ...result, isValid: true });
      return { ...result, isValid: true };
    } catch (error) {
      console.error('Fuel VAT calculation error:', error);
      return { baseAmount: 0, vatAmount: 0, total: 0, isValid: false, error: `Calculation error: ${error.message}` };
    }
  }, []);

  // Calculate VAT breakdown for maintenance with enhanced validation
  const calculateMaintenanceVAT = useCallback((amount, includesVAT) => {
    // Handle edge cases
    if (!amount || amount <= 0 || isNaN(amount)) {
      return { baseAmount: 0, vatAmount: 0, total: 0, isValid: true };
    }
    
    // Validate amount is within reasonable bounds
    if (amount > 999999.99) {
      return { baseAmount: 0, vatAmount: 0, total: 0, isValid: false, error: 'Amount exceeds maximum allowed value' };
    }
    
    try {
      const result = calculateVATBreakdown(amount, includesVAT);
      
      // More lenient validation with better error handling
      const tolerance = 0.05; // Increased tolerance for rounding differences
      
      if (includesVAT) {
        // For inclusive VAT, the result.total should equal the entered amount
        const difference = Math.abs(amount - result.total);
        if (difference > tolerance) {
          console.warn('Maintenance VAT calculation difference:', { amount, resultTotal: result.total, difference });
          // Still return the calculation but mark as potentially invalid
          return { 
            ...result, 
            isValid: true, // Allow it to proceed
            warning: `Minor calculation difference: ${difference.toFixed(3)}`
          };
        }
      } else {
        // For exclusive VAT, validate that base + VAT = total
        const calculatedTotal = result.baseAmount + result.vatAmount;
        const difference = Math.abs(result.total - calculatedTotal);
        if (difference > tolerance) {
          console.warn('Maintenance VAT total calculation difference:', { 
            resultTotal: result.total, 
            calculatedTotal, 
            difference 
          });
          // Still return the calculation but mark as potentially invalid
          return { 
            ...result, 
            isValid: true, // Allow it to proceed
            warning: `Minor total calculation difference: ${difference.toFixed(3)}`
          };
        }
      }
      
      return { ...result, isValid: true };
    } catch (error) {
      console.error('Maintenance VAT calculation error:', error);
      return { baseAmount: 0, vatAmount: 0, total: 0, isValid: false, error: `Calculation error: ${error.message}` };
    }
  }, []);

  // Update VAT preference and save to user preferences
  const updateVATPreference = async (costType, includesVAT) => {
    try {
      await userPreferencesService.updateVATPreference(user.uid, currentCompany.id, costType, includesVAT);
      setVatPreferences(prev => ({
        ...prev,
        [`${costType}VATDefault`]: includesVAT
      }));
    } catch (error) {
      console.error('Error updating VAT preference:', error);
      toast.error('Failed to save VAT preference');
    }
  };



  // Helper function to auto-update costing status when costs are modified
  const updateCostingStatus = (currentData, newData) => {
    // Check if any cost values have been entered or modified
    const hasFuel = parseFloat(newData.fuel || 0) > 0;
    const hasMaintenance = parseFloat(newData.maintenance || 0) > 0;
    const hasCommission = parseFloat(newData.commission || 0) > 0;
    const hasStops = (newData.stops || []).some(stop => parseFloat(stop.amount || 0) > 0);
    const hasOtherCosts = (newData.otherCosts || []).some(cost => parseFloat(cost.amount || 0) > 0);

    // If any costs are entered and status is "not_added", change to "partial"
    if ((hasFuel || hasMaintenance || hasCommission || hasStops || hasOtherCosts) &&
      newData.costingStatus === 'not_added') {
      return 'partial';
    }

    // Keep the current status if already set to partial or completed
    return newData.costingStatus;
  };

  // Removed showCostingUpdateNotification function as it was causing annoying notifications

  // Helper function to check if costing has been edited after posting
  const checkIfEditedAfterPosting = (currentData) => {
    if (!originalPostedData) return false;

    // Compare key fields
    const fieldsToCompare = ['fuel', 'maintenance', 'commission'];
    for (const field of fieldsToCompare) {
      if (parseFloat(currentData[field] || 0) !== parseFloat(originalPostedData[field] || 0)) {
        return true;
      }
    }

    // Compare stops
    const currentStops = currentData.stops || [];
    const originalStops = originalPostedData.stops || [];
    if (currentStops.length !== originalStops.length) return true;

    for (let i = 0; i < currentStops.length; i++) {
      if (parseFloat(currentStops[i]?.amount || 0) !== parseFloat(originalStops[i]?.amount || 0) ||
        currentStops[i]?.description !== originalStops[i]?.description) {
        return true;
      }
    }

    // Compare other costs
    const currentOtherCosts = currentData.otherCosts || [];
    const originalOtherCosts = originalPostedData.otherCosts || [];
    if (currentOtherCosts.length !== originalOtherCosts.length) return true;

    for (let i = 0; i < currentOtherCosts.length; i++) {
      if (parseFloat(currentOtherCosts[i]?.amount || 0) !== parseFloat(originalOtherCosts[i]?.amount || 0) ||
        currentOtherCosts[i]?.description !== originalOtherCosts[i]?.description) {
        return true;
      }
    }

    return false;
  };

  // Get costing status badge styles
  const getCostingStatusBadge = (status) => {
    const styles = {
      not_added: 'bg-red-100 text-red-800',
      partial: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  // Get costing status display text
  const getCostingStatusText = (status) => {
    const texts = {
      not_added: t('accounting.costing_not_added'),
      partial: t('accounting.costing_partial'),
      completed: t('accounting.costing_completed')
    };
    return texts[status] || status;
  };

  // Handle opening costing modal
  const openCostingModal = (shipment) => {
    setSelectedShipment(shipment);
    const existingCosting = getShipmentCosting(shipment.id);

    if (existingCosting) {
      // Use existing VAT settings or defaults from preferences
      const fuelIncludesVAT = existingCosting.fuelIncludesVAT !== undefined 
        ? existingCosting.fuelIncludesVAT 
        : vatPreferences.fuelVATDefault;
      const maintenanceIncludesVAT = existingCosting.maintenanceIncludesVAT !== undefined 
        ? existingCosting.maintenanceIncludesVAT 
        : vatPreferences.maintenanceVATDefault;

      // Calculate VAT breakdown for existing amounts
      const maintenanceAmount = existingCosting.maintenanceAmount || existingCosting.maintenance || 0;
      
      const maintenanceVAT = calculateMaintenanceVAT(maintenanceAmount, maintenanceIncludesVAT);

      const costingDataToSet = {
        // Legacy fuel field for backward compatibility (use total fuel amount)
        fuel: existingCosting.totalFuelAmount || existingCosting.fuel || '',
        fuelIncludesVAT: true, // Default to true for legacy compatibility
        fuelBaseAmount: existingCosting.totalFuelBaseAmount || 0,
        fuelVATAmount: existingCosting.totalFuelVATAmount || 0,
        
        // Separated fuel costs
        petroAppFuelAmount: existingCosting.petroAppFuelAmount || 0,
        petroAppFuelIncludesVAT: existingCosting.petroAppFuelIncludesVAT !== undefined ? existingCosting.petroAppFuelIncludesVAT : true,
        petroAppFuelBaseAmount: existingCosting.petroAppFuelBaseAmount || 0,
        petroAppFuelVATAmount: existingCosting.petroAppFuelVATAmount || 0,
        appliedFuelTransactions: existingCosting.appliedFuelTransactions || [],
        
        manualFuelAmount: existingCosting.manualFuelAmount || 0,
        manualFuelIncludesVAT: existingCosting.manualFuelIncludesVAT !== undefined ? existingCosting.manualFuelIncludesVAT : vatPreferences.fuelVATDefault,
        manualFuelBaseAmount: existingCosting.manualFuelBaseAmount || 0,
        manualFuelVATAmount: existingCosting.manualFuelVATAmount || 0,
        
        // Combined totals
        totalFuelAmount: existingCosting.totalFuelAmount || 0,
        totalFuelBaseAmount: existingCosting.totalFuelBaseAmount || 0,
        totalFuelVATAmount: existingCosting.totalFuelVATAmount || 0,
        
        maintenance: existingCosting.maintenanceAmount || existingCosting.maintenance || '',
        maintenanceIncludesVAT,
        maintenanceBaseAmount: existingCosting.maintenanceBaseAmount || maintenanceVAT.baseAmount,
        maintenanceVATAmount: existingCosting.maintenanceVATAmount || maintenanceVAT.vatAmount,
        stops: existingCosting.stops || [],
        otherCosts: existingCosting.otherCosts || [],
        commission: existingCosting.commissionAmount || existingCosting.commission || '',
        notes: existingCosting.notes || '',
        costingStatus: existingCosting.costingStatus || 'not_added'
      };

      setCostingData(costingDataToSet);

      // Track if this costing has been posted for edit detection
      if (existingCosting.isPosted) {
        setOriginalPostedData(JSON.parse(JSON.stringify(costingDataToSet)));
        setEditedAfterPosting(existingCosting.editedAfterPosting === true); // Use the actual flag from database
      } else {
        setOriginalPostedData(null);
        setEditedAfterPosting(false);
      }
    } else {
      // New costing - use user preferences as defaults
      setCostingData({
        // Legacy fuel field for backward compatibility
        fuel: '',
        fuelIncludesVAT: true, // Default to true for legacy compatibility
        fuelBaseAmount: 0,
        fuelVATAmount: 0,
        
        // Separated fuel costs
        petroAppFuelAmount: 0,
        petroAppFuelIncludesVAT: true,
        petroAppFuelBaseAmount: 0,
        petroAppFuelVATAmount: 0,
        appliedFuelTransactions: [],
        
        manualFuelAmount: 0,
        manualFuelIncludesVAT: vatPreferences.fuelVATDefault,
        manualFuelBaseAmount: 0,
        manualFuelVATAmount: 0,
        
        // Combined totals
        totalFuelAmount: 0,
        totalFuelBaseAmount: 0,
        totalFuelVATAmount: 0,
        
        maintenance: '',
        maintenanceIncludesVAT: vatPreferences.maintenanceVATDefault,
        maintenanceBaseAmount: 0,
        maintenanceVATAmount: 0,
        stops: [],
        otherCosts: [],
        commission: '',
        notes: '',
        costingStatus: 'not_added'
      });
      setOriginalPostedData(null);
      setEditedAfterPosting(false);
    }
    
    // Reset validation errors when opening modal
    setValidationErrors({
      fuel: null,
      maintenance: null,
      commission: null,
      stops: [],
      otherCosts: []
    });
    
    setShowCostingModal(true);
  };

  // Helper function to generate expense ID
  const generateExpenseId = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const timestamp = Date.now().toString().slice(-6);

    return `EXP-${year}${month}${day}-${timestamp}`;
  };

  // Helper function to create or update automatic expenses from costing
  const createOrUpdateAutomaticExpenses = async (costingPayload, shipment, automationSettings) => {
    const processedExpenses = [];

    try {
      // Get existing auto-generated expenses for this shipment
      const existingExpenses = await expensesService.getExpenses(currentCompany.id);
      const shipmentExpenses = existingExpenses.filter(exp =>
        exp.shipmentId === shipment.id && exp.autoGenerated
      );

      // ✅ DEFINE EXPENSE TYPES WITH VAT BREAKDOWN FROM COSTING
      const expenseTypes = [
        {
          category: 'fuel',
          baseAmount: costingPayload.fuelBaseAmount || 0,
          vatAmount: costingPayload.fuelVATAmount || 0,
          totalAmount: (costingPayload.fuelBaseAmount || 0) + (costingPayload.fuelVATAmount || 0),
          description: `Fuel expense for shipment ${shipment.trackingNumber} (${shipment.origin} → ${shipment.destination})`,
          vatIncluded: costingPayload.fuelIncludesVAT,
          automationKey: 'fuelExpenses',
          enabled: automationSettings?.fuelExpenses?.expenseEnabled !== false
        },
        {
          category: 'maintenance',
          baseAmount: costingPayload.maintenanceBaseAmount || 0,
          vatAmount: costingPayload.maintenanceVATAmount || 0,
          totalAmount: (costingPayload.maintenanceBaseAmount || 0) + (costingPayload.maintenanceVATAmount || 0),
          description: `Maintenance expense for shipment ${shipment.trackingNumber} (${shipment.origin} → ${shipment.destination})`,
          vatIncluded: costingPayload.maintenanceIncludesVAT,
          automationKey: 'maintenanceExpenses',
          enabled: automationSettings?.maintenanceExpenses?.expenseEnabled !== false
        },
        {
          category: 'salary',
          baseAmount: costingPayload.commission || 0,
          vatAmount: 0, // Commission is VAT-exempt
          totalAmount: costingPayload.commission || 0,
          description: `Commission payment for driver - Shipment ${shipment.trackingNumber}`,
          vatIncluded: false, // Commission is always VAT-exempt
          driverId: shipment.driverId,
          automationKey: 'commissionExpenses',
          enabled: automationSettings?.commissionExpenses?.expenseEnabled !== false
        }
      ];

      for (const expenseType of expenseTypes) {
        // ✅ CHECK AUTOMATION SETTINGS BEFORE PROCESSING
        if (expenseType.totalAmount > 0 && expenseType.enabled) {
          console.log(`✅ Processing ${expenseType.category} expense (automation enabled):`, expenseType.totalAmount);
        } else if (expenseType.totalAmount > 0 && !expenseType.enabled) {
          console.log(`⚠️ Skipping ${expenseType.category} expense (automation disabled):`, expenseType.totalAmount);
          processedExpenses.push(`skipped_${expenseType.category}_disabled`);
          continue;
        } else {
          continue; // Skip if amount is 0
        }

        if (expenseType.totalAmount > 0) {
          // Find existing expense of this type
          const existingExpense = shipmentExpenses.find(exp =>
            exp.category === expenseType.category &&
            (!expenseType.driverId || exp.driverId === expenseType.driverId)
          );

          if (existingExpense) {
            // Update existing expense if amounts changed (compare base, VAT, or total)
            const needsUpdate = 
              existingExpense.baseAmount !== expenseType.baseAmount ||
              existingExpense.vatAmount !== expenseType.vatAmount ||
              existingExpense.totalAmount !== expenseType.totalAmount;

            if (needsUpdate) {
              console.log(`🔄 Updating ${expenseType.category} expense - Base: ${expenseType.baseAmount}, VAT: ${expenseType.vatAmount}, Total: ${expenseType.totalAmount}`);

              await expensesService.updateExpense(existingExpense.id, {
                baseAmount: expenseType.baseAmount,
                vatAmount: expenseType.vatAmount,
                totalAmount: expenseType.totalAmount,
                amount: expenseType.totalAmount, // Keep legacy field for compatibility
                vatIncluded: expenseType.vatIncluded,
                description: expenseType.description,
                updatedBy: user.uid,
                updatedAt: new Date(),
                vatCalculationValid: true,
                calculationMethod: expenseType.vatIncluded ? 'inclusive' : 'exclusive'
              });

              processedExpenses.push(`updated_${expenseType.category}`);
            } else {
              processedExpenses.push(`existing_${expenseType.category}`);
            }
          } else {
            // Create new expense with VAT breakdown
            console.log(`🔄 Creating new ${expenseType.category} expense - Base: ${expenseType.baseAmount}, VAT: ${expenseType.vatAmount}, Total: ${expenseType.totalAmount}`);

            const expenseData = {
              id: generateExpenseId(),
              companyId: currentCompany.id,
              date: new Date().toISOString().split('T')[0],
              category: expenseType.category,
              vendor: expenseType.category === 'salary' ? (shipment.driverName || t('accounting.driver_commission')) :
                expenseType.category === 'maintenance' ? t('accounting.maintenance_service') :
                  expenseType.category === 'fuel' ? t('accounting.fuel_station') : t('accounting.system_generated'),
              description: expenseType.description,
              
              // Enhanced VAT breakdown fields
              baseAmount: expenseType.baseAmount,
              vatAmount: expenseType.vatAmount,
              totalAmount: expenseType.totalAmount,
              amount: expenseType.totalAmount, // Keep legacy field for compatibility
              vatIncluded: expenseType.vatIncluded,
              vatCalculationValid: true,
              calculationMethod: expenseType.vatIncluded ? 'inclusive' : 'exclusive',
              
              paymentMethod: 'cash',
              reference: `Shipment: ${shipment.trackingNumber}`,
              notes: `Auto-generated from shipment costing with VAT breakdown`,
              status: 'approved',
              receiptAttached: false,
              receiptUrl: null,
              receiptFileName: null,
              createdBy: user.uid,
              submittedAt: new Date().toISOString(),
              shipmentId: shipment.id,
              autoGenerated: true,
              driverName: shipment.driverName || null,
              truckNumber: shipment.vehiclePlate || shipment.truckNumber || null,
              relatedShipmentId: shipment.id
            };

            if (expenseType.driverId) {
              expenseData.driverId = expenseType.driverId;
            }

            // Create expense without journal entry (skipJournalEntry = true)
            await expensesService.addExpense(expenseData, true);
            processedExpenses.push(`created_${expenseType.category}`);
          }
        } else {
          // Remove expense if total amount is now 0
          const existingExpense = shipmentExpenses.find(exp =>
            exp.category === expenseType.category &&
            (!expenseType.driverId || exp.driverId === expenseType.driverId)
          );

          if (existingExpense) {
            console.log(`🔄 Removing ${expenseType.category} expense (total amount is now 0)`);
            await expensesService.deleteExpense(existingExpense.id);
            processedExpenses.push(`deleted_${expenseType.category}`);
          }
        }
      }

      return processedExpenses;
    } catch (error) {
      console.error('Error creating/updating automatic expenses:', error);
      throw error;
    }
  };

  // Validate all costing data before submission
  const validateCostingData = () => {
    const errors = [];
    
    // Check for validation errors
    if (validationErrors.fuel) {
      errors.push(`Fuel: ${validationErrors.fuel}`);
    }
    if (validationErrors.maintenance) {
      errors.push(`Maintenance: ${validationErrors.maintenance}`);
    }
    if (validationErrors.commission) {
      errors.push(`Commission: ${validationErrors.commission}`);
    }
    
    // Validate VAT calculations
    if (costingData.fuel > 0) {
      const fuelVAT = calculateFuelVAT(costingData.fuel, costingData.fuelIncludesVAT);
      if (!fuelVAT.isValid) {
        errors.push(`Fuel VAT calculation: ${fuelVAT.error}`);
      }
    }
    
    if (costingData.maintenance > 0) {
      const maintenanceVAT = calculateMaintenanceVAT(costingData.maintenance, costingData.maintenanceIncludesVAT);
      if (!maintenanceVAT.isValid) {
        errors.push(`Maintenance VAT calculation: ${maintenanceVAT.error}`);
      }
    }
    
    // Validate stops
    costingData.stops.forEach((stop, index) => {
      if (!stop.description || stop.description.trim() === '') {
        errors.push(`Stop ${index + 1}: Description is required`);
      }
      if (!stop.amount || parseFloat(stop.amount) <= 0) {
        errors.push(`Stop ${index + 1}: Amount must be greater than 0`);
      }
      if (parseFloat(stop.amount) > 999999.99) {
        errors.push(`Stop ${index + 1}: Amount exceeds maximum allowed value`);
      }
    });
    
    // Validate other costs
    costingData.otherCosts.forEach((cost, index) => {
      if (!cost.description || cost.description.trim() === '') {
        errors.push(`Other cost ${index + 1}: Description is required`);
      }
      if (!cost.amount || parseFloat(cost.amount) <= 0) {
        errors.push(`Other cost ${index + 1}: Amount must be greater than 0`);
      }
      if (parseFloat(cost.amount) > 999999.99) {
        errors.push(`Other cost ${index + 1}: Amount exceeds maximum allowed value`);
      }
    });
    
    return errors;
  };

  // Handle saving costing data
  const handleSaveCosting = async () => {
    if (!selectedShipment) return;

    // Validate all data before submission
    const validationErrors = validateCostingData();
    if (validationErrors.length > 0) {
      toast.error(`Please fix the following errors:\n${validationErrors.join('\n')}`);
      return;
    }

    try {
      // Parse entered amounts safely
      // Handle separated fuel costs
      const petroAppFuelEntered = toNumber(costingData.petroAppFuelAmount);
      const manualFuelEntered = toNumber(costingData.manualFuelAmount);
      const totalFuelEntered = (petroAppFuelEntered || 0) + (manualFuelEntered || 0);
      
      // Legacy fuel field for backward compatibility
      const fuelEntered = totalFuelEntered;
      const maintenanceEntered = toNumber(costingData.maintenance);
      const commissionEntered = toNumber(costingData.commission);

      // Guard against invalid VAT-inclusive amounts for manual fuel
      if (costingData.manualFuelIncludesVAT) {
        const expected = +(costingData.manualFuelBaseAmount + costingData.manualFuelVATAmount).toFixed(2);
        const got = +((manualFuelEntered ?? expected)).toFixed(2);
        if (expected > 0 && got === 0) {
          toast.error('Manual fuel amount is invalid. Enter a valid number.');
          return;
        }
      }

      // Guard against invalid VAT-inclusive amounts for PetroApp fuel
      if (costingData.petroAppFuelIncludesVAT) {
        const expected = +(costingData.petroAppFuelBaseAmount + costingData.petroAppFuelVATAmount).toFixed(2);
        const got = +((petroAppFuelEntered ?? expected)).toFixed(2);
        if (expected > 0 && got === 0) {
          toast.error('PetroApp fuel amount is invalid. Enter a valid number.');
          return;
        }
      }

      // Guard against invalid VAT-inclusive amounts for maintenance
      if (costingData.maintenanceIncludesVAT) {
        const expected = +(costingData.maintenanceBaseAmount + costingData.maintenanceVATAmount).toFixed(2);
        const got = +((maintenanceEntered ?? expected)).toFixed(2);
        if (expected > 0 && got === 0) {
          toast.error('Maintenance amount is invalid. Enter a valid number.');
          return;
        }
      }

      const costingPayload = {
        shipmentId: selectedShipment.id,
        companyId: currentCompany.id,
        driverId: selectedShipment.driverId, // Add driver ID for commission tracking
        
        // Separated fuel costs
        petroAppFuelAmount: petroAppFuelEntered ?? 0,
        petroAppFuelIncludesVAT: costingData.petroAppFuelIncludesVAT,
        petroAppFuelBaseAmount: costingData.petroAppFuelBaseAmount,
        petroAppFuelVATAmount: costingData.petroAppFuelVATAmount,
        appliedFuelTransactions: costingData.appliedFuelTransactions || [],
        
        manualFuelAmount: manualFuelEntered ?? 0,
        manualFuelIncludesVAT: costingData.manualFuelIncludesVAT,
        manualFuelBaseAmount: costingData.manualFuelBaseAmount,
        manualFuelVATAmount: costingData.manualFuelVATAmount,
        
        // Combined fuel totals
        totalFuelAmount: totalFuelEntered,
        totalFuelBaseAmount: (costingData.petroAppFuelBaseAmount || 0) + (costingData.manualFuelBaseAmount || 0),
        totalFuelVATAmount: (costingData.petroAppFuelVATAmount || 0) + (costingData.manualFuelVATAmount || 0),
        
        // Legacy fuel fields for backward compatibility
        fuelAmount: totalFuelEntered,
        fuelIncludesVAT: costingData.fuelIncludesVAT,
        fuelBaseAmount: (costingData.petroAppFuelBaseAmount || 0) + (costingData.manualFuelBaseAmount || 0),
        fuelVATAmount: (costingData.petroAppFuelVATAmount || 0) + (costingData.manualFuelVATAmount || 0),
        
        maintenanceAmount: maintenanceEntered ?? (costingData.maintenanceBaseAmount + costingData.maintenanceVATAmount),
        maintenanceIncludesVAT: costingData.maintenanceIncludesVAT,
        maintenanceBaseAmount: costingData.maintenanceBaseAmount,
        maintenanceVATAmount: costingData.maintenanceVATAmount,
        
        commissionAmount: commissionEntered ?? 0,
        
        // Backward compatibility (optional)
        fuel: fuelEntered ?? undefined,
        maintenance: maintenanceEntered ?? undefined,
        commission: commissionEntered ?? undefined,
        
        stops: costingData.stops,
        otherCosts: costingData.otherCosts,
        notes: costingData.notes || '',
        costingStatus: costingData.costingStatus || 'not_added',
        updatedBy: user.uid,
        updatedAt: new Date()
      };

      const existingCosting = getShipmentCosting(selectedShipment.id);

      // Save costing data
      if (existingCosting) {
        // Add flag if this is an edit after posting
        if (editedAfterPosting) {
          costingPayload.editedAfterPosting = true;
        }
        await shipmentCostingsService.updateCosting(existingCosting.id, costingPayload);
        toast.success('Costing updated successfully');
      } else {
        costingPayload.createdBy = user.uid;
        costingPayload.createdAt = new Date();
        await shipmentCostingsService.addCosting(costingPayload);
        toast.success('Costing created successfully');
      }

      // **NEW:** Update the shipment with the commission amount from costing
      // This ensures the driver commission system uses the actual amount instead of calculating percentages
      await shipmentsService.updateShipment(selectedShipment.id, {
        commissionAmount: parseFloat(costingData.commission) || 0,
        costingUpdatedAt: new Date(),
        costingUpdatedBy: user.uid
      });

      // Provide user feedback about the costing update
      if (existingCosting) {
        if (editedAfterPosting) {
          toast.success(t('accounting.costing_updated_needs_refinalize'));
        } else {
          toast.success('Costing updated successfully! Use "Finalize & Post" to create expenses and journal entries.');
        }
      } else {
        toast.success('Costing created successfully! Use "Finalize & Post" to create expenses and journal entries.');
      }

      await loadData();
      setShowCostingModal(false);
    } catch (error) {
      console.error('Error saving costing:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to save costing data';
      if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      if (error.code) {
        errorMessage += ` (Code: ${error.code})`;
      }
      
      toast.error(errorMessage);
    }
  };

  // Handle adding stop
  const addStop = () => {
    setCostingData(prev => ({
      ...prev,
      stops: [...prev.stops, { description: '', quantity: 1, amount: '' }]
    }));
  };

  // Handle updating stop with validation
  const updateStop = (index, field, value) => {
    setCostingData(prev => {
      let processedValue = value;
      let stopError = null;

      // Convert Arabic numerals to English for amount fields
      if (field === 'amount' && typeof value === 'string') {
        processedValue = ensureEnglishNumerals(value);
      }

      // Validate stop data
      if (field === 'amount') {
        const numericValue = parseFloat(processedValue) || 0;
        if (numericValue < 0) {
          stopError = 'Amount cannot be negative';
        } else if (numericValue > 999999.99) {
          stopError = 'Amount exceeds maximum allowed value';
        } else if (isNaN(numericValue) && processedValue !== '') {
          stopError = 'Please enter a valid number';
        }
        processedValue = numericValue;
      } else if (field === 'description') {
        if (processedValue && processedValue.length > 200) {
          stopError = 'Description is too long (max 200 characters)';
        }
      } else if (field === 'quantity') {
        const numericValue = parseInt(processedValue) || 1;
        if (numericValue < 1) {
          stopError = 'Quantity must be at least 1';
        } else if (numericValue > 999) {
          stopError = 'Quantity exceeds maximum allowed value';
        }
        processedValue = numericValue;
      }

      // Update validation errors for stops
      setValidationErrors(prevErrors => {
        const newStopsErrors = [...prevErrors.stops];
        newStopsErrors[index] = stopError;
        return {
          ...prevErrors,
          stops: newStopsErrors
        };
      });

      const updatedStops = prev.stops.map((stop, i) =>
        i === index ? { ...stop, [field]: processedValue } : stop
      );
      const newData = { ...prev, stops: updatedStops };

      // Check if edited after posting
      const wasEdited = checkIfEditedAfterPosting(newData);
      setEditedAfterPosting(wasEdited);

      // Auto-update status if amount field is changed and has value
      if (field === 'amount' && parseFloat(processedValue || 0) > 0) {
        const newStatus = updateCostingStatus(prev, newData);
        return { ...newData, costingStatus: newStatus };
      }

      return newData;
    });
  };

  // Handle removing stop
  const removeStop = (index) => {
    setCostingData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
  };

  // Handle adding other cost
  const addOtherCost = () => {
    setCostingData(prev => ({
      ...prev,
      otherCosts: [...prev.otherCosts, { description: '', amount: '' }]
    }));
  };

  // Handle updating other cost with validation
  const updateOtherCost = (index, field, value) => {
    setCostingData(prev => {
      let processedValue = value;
      let costError = null;

      // Convert Arabic numerals to English for amount fields
      if (field === 'amount' && typeof value === 'string') {
        processedValue = ensureEnglishNumerals(value);
      }

      // Validate other cost data
      if (field === 'amount') {
        const numericValue = parseFloat(processedValue) || 0;
        if (numericValue < 0) {
          costError = 'Amount cannot be negative';
        } else if (numericValue > 999999.99) {
          costError = 'Amount exceeds maximum allowed value';
        } else if (isNaN(numericValue) && processedValue !== '') {
          costError = 'Please enter a valid number';
        }
        processedValue = numericValue;
      } else if (field === 'description') {
        if (processedValue && processedValue.length > 200) {
          costError = 'Description is too long (max 200 characters)';
        }
      }

      // Update validation errors for other costs
      setValidationErrors(prevErrors => {
        const newOtherCostsErrors = [...prevErrors.otherCosts];
        newOtherCostsErrors[index] = costError;
        return {
          ...prevErrors,
          otherCosts: newOtherCostsErrors
        };
      });

      const updatedOtherCosts = prev.otherCosts.map((cost, i) =>
        i === index ? { ...cost, [field]: processedValue } : cost
      );
      const newData = { ...prev, otherCosts: updatedOtherCosts };

      // Check if edited after posting
      const wasEdited = checkIfEditedAfterPosting(newData);
      setEditedAfterPosting(wasEdited);

      // Auto-update status if amount field is changed and has value
      if (field === 'amount' && parseFloat(processedValue || 0) > 0) {
        const newStatus = updateCostingStatus(prev, newData);
        return { ...newData, costingStatus: newStatus };
      }

      return newData;
    });
  };

  // Handle removing other cost
  const removeOtherCost = (index) => {
    setCostingData(prev => ({
      ...prev,
      otherCosts: prev.otherCosts.filter((_, i) => i !== index)
    }));
  };

  // Handle finalize and post
  const handleFinalizeAndPost = async (shipment) => {
    const costing = getShipmentCosting(shipment.id);
    if (!costing) {
      toast.error('No costing data found for this shipment');
      return;
    }

    try {
      console.log('🔄 Starting finalize and post for shipment:', shipment.trackingNumber);

      // Validate costing data
      const totalCost = await calculateTotalCost(costing, shipment.id);
      if (totalCost === 0) {
        toast.error('Cannot finalize costing with zero costs. Please add some costs first.');
        return;
      }

      // ✅ CHECK AUTOMATION SETTINGS BEFORE PROCESSING
      console.log('🔄 Checking automation settings...');
      const automationSettings = await accountMappingService.getAutomationSettings(currentCompany.id);
      console.log('📋 Automation settings:', automationSettings);

      // Create automatic expenses from costing data with VAT breakdown
      const fuelAmount = parseFloat(costing.fuel) || 0;
      const maintenanceAmount = parseFloat(costing.maintenance) || 0;
      const commissionAmount = parseFloat(costing.commission) || 0;
      
      // Calculate VAT breakdown for each cost type
      const fuelVAT = calculateFuelVAT(fuelAmount, costing.fuelIncludesVAT);
      const maintenanceVAT = calculateMaintenanceVAT(maintenanceAmount, costing.maintenanceIncludesVAT);
      
      const costingPayload = {
        fuel: fuelAmount,
        fuelIncludesVAT: costing.fuelIncludesVAT,
        fuelBaseAmount: fuelVAT.baseAmount,
        fuelVATAmount: fuelVAT.vatAmount,
        
        maintenance: maintenanceAmount,
        maintenanceIncludesVAT: costing.maintenanceIncludesVAT,
        maintenanceBaseAmount: maintenanceVAT.baseAmount,
        maintenanceVATAmount: maintenanceVAT.vatAmount,
        
        commission: commissionAmount, // Commission is VAT-exempt
        stops: costing.stops || [],
        otherCosts: costing.otherCosts || []
      };

      console.log('🔄 Costing payload:', costingPayload);

      // ✅ CREATE/UPDATE EXPENSES ONLY IF AUTOMATION IS ENABLED
      let processedExpenses = [];
      try {
        processedExpenses = await createOrUpdateAutomaticExpenses(costingPayload, shipment, automationSettings);
        console.log('✅ Expenses processed:', processedExpenses);
      } catch (expenseError) {
        console.error('❌ Error processing expenses:', expenseError);
        toast.error(`Failed to process expenses: ${expenseError.message}`);
        return;
      }

      // Create comprehensive journal entry for all shipment costs
      try {
        // Ensure chart of accounts is initialized before creating journal entries
        console.log('🔄 Checking chart of accounts for company:', currentCompany.id);
        const accounts = await chartOfAccountsService.getChartOfAccounts(currentCompany.id);
        if (accounts.length === 0) {
          console.log('⚠️ Chart of accounts not found, initializing...');
          await chartOfAccountsService.initializeDefaultChartOfAccounts(currentCompany.id, user.uid);
          console.log('✅ Chart of accounts initialized');
        }

        // Ensure account mappings are initialized
        console.log('🔄 Checking account mappings for company:', currentCompany.id);
        const mappings = await accountMappingService.getAccountMappings(currentCompany.id);
        console.log('✅ Account mappings found:', mappings);

        // Prepare costing data for journal entry with VAT breakdown
        const costingForJournal = {
          fuelCost: costingPayload.fuel || 0,
          fuelBaseAmount: costingPayload.fuelBaseAmount || 0,
          fuelVATAmount: costingPayload.fuelVATAmount || 0,
          fuelIncludesVAT: costingPayload.fuelIncludesVAT,
          
          maintenanceCost: costingPayload.maintenance || 0,
          maintenanceBaseAmount: costingPayload.maintenanceBaseAmount || 0,
          maintenanceVATAmount: costingPayload.maintenanceVATAmount || 0,
          maintenanceIncludesVAT: costingPayload.maintenanceIncludesVAT,
          
          driverSalary: 0, // Driver salary is handled separately
          commissionCost: costingPayload.commission || 0, // Commission is VAT-exempt
          otherCosts: 0, // Handle stops and other costs later
          date: new Date().toISOString().split('T')[0]
        };

        // Add stops and other costs
        const stopsCost = (costingPayload.stops || []).reduce((sum, stop) => sum + (parseFloat(stop.amount) || 0), 0);
        const otherCost = (costingPayload.otherCosts || []).reduce((sum, cost) => sum + (parseFloat(cost.amount) || 0), 0);
        costingForJournal.otherCosts = stopsCost + otherCost;

        // ✅ CHECK AUTOMATION SETTINGS FOR JOURNAL ENTRIES
        const enabledCosts = {};
        let totalEnabledCosts = 0;

        // Check each cost type against automation settings
        if (automationSettings.fuelExpenses?.journalEntryEnabled && costingForJournal.fuelCost > 0) {
          enabledCosts.fuelCost = costingForJournal.fuelCost;
          totalEnabledCosts += costingForJournal.fuelCost;
          console.log('✅ Fuel journal entries enabled:', costingForJournal.fuelCost);
        } else if (costingForJournal.fuelCost > 0) {
          console.log('⚠️ Fuel journal entries disabled, skipping:', costingForJournal.fuelCost);
        }

        if (automationSettings.maintenanceExpenses?.journalEntryEnabled && costingForJournal.maintenanceCost > 0) {
          enabledCosts.maintenanceCost = costingForJournal.maintenanceCost;
          totalEnabledCosts += costingForJournal.maintenanceCost;
          console.log('✅ Maintenance journal entries enabled:', costingForJournal.maintenanceCost);
        } else if (costingForJournal.maintenanceCost > 0) {
          console.log('⚠️ Maintenance journal entries disabled, skipping:', costingForJournal.maintenanceCost);
        }

        if (automationSettings.commissionExpenses?.journalEntryEnabled && costingForJournal.commissionCost > 0) {
          enabledCosts.commissionCost = costingForJournal.commissionCost;
          totalEnabledCosts += costingForJournal.commissionCost;
          console.log('✅ Commission journal entries enabled:', costingForJournal.commissionCost);
        } else if (costingForJournal.commissionCost > 0) {
          console.log('⚠️ Commission journal entries disabled, skipping:', costingForJournal.commissionCost);
        }

        // Other costs are always processed if they exist (no specific automation setting)
        if (costingForJournal.otherCosts > 0) {
          enabledCosts.otherCosts = costingForJournal.otherCosts;
          totalEnabledCosts += costingForJournal.otherCosts;
          console.log('✅ Other costs included:', costingForJournal.otherCosts);
        }

        if (totalEnabledCosts > 0) {
          // Create journal entry only for enabled cost types
          console.log('🔄 Attempting to create journal entry for shipment:', shipment.trackingNumber);
          console.log('🔄 Enabled costs for journal:', enabledCosts);

          await journalEntriesService.createShipmentCostingJournalEntry(
            currentCompany.id,
            shipment,
            { ...costingForJournal, ...enabledCosts, date: costingForJournal.date },
            user.uid,
            automationSettings
          );

          console.log('✅ Journal entry created successfully');
        } else {
          console.log('⚠️ No journal entries enabled or no costs to record');
          // Don't show a separate toast here - will be included in the final success message
        }

        // Mark costing as posted and clear edited flag
        await shipmentCostingsService.updateCosting(costing.id, {
          isPosted: true,
          postedAt: new Date(),
          postedBy: user.uid,
          editedAfterPosting: false, // Clear the edited flag
          costingStatus: 'completed' // Update status to completed
        });

        // Reset edited state since we just re-finalized
        setEditedAfterPosting(false);
        setOriginalPostedData(null); // Clear original data since we just posted

        // ✅ PROVIDE DETAILED SUCCESS MESSAGE BASED ON AUTOMATION SETTINGS
        const skippedExpenses = processedExpenses.filter(exp => exp.includes('skipped')).length;
        const createdExpenses = processedExpenses.filter(exp => exp.includes('created') || exp.includes('updated')).length;

        let successMessage = '';
        if (editedAfterPosting) {
          successMessage = t('accounting.costing_refinalized_successfully', 'Costing re-finalized successfully!');
        } else {
          const parts = [];
          if (createdExpenses > 0) {
            parts.push(`${createdExpenses} expense(s) processed`);
          }
          if (totalEnabledCosts > 0) {
            parts.push('journal entry created');
          } else if (costingForJournal.fuelCost > 0 || costingForJournal.maintenanceCost > 0 || costingForJournal.commissionCost > 0) {
            parts.push('journal entries skipped (automation disabled)');
          }
          if (skippedExpenses > 0) {
            parts.push(`${skippedExpenses} expense(s) skipped (automation disabled)`);
          }

          if (parts.length > 0) {
            successMessage = `✅ Costing finalized! ${parts.join(', ')}.`;
          } else {
            successMessage = '✅ Costing finalized! All automation is disabled - no entries created.';
          }
        }

        toast.success(successMessage);

      } catch (journalError) {
        console.error('❌ Journal entry creation failed:', journalError);

        // Still mark as posted even if journal entry fails
        await shipmentCostingsService.updateCosting(costing.id, {
          isPosted: true,
          postedAt: new Date(),
          postedBy: user.uid,
          editedAfterPosting: false,
          costingStatus: 'completed'
        });

        // Reset edited state
        setEditedAfterPosting(false);
        setOriginalPostedData(null);

        // ✅ PROVIDE CLEARER ERROR MESSAGING
        const skippedExpenses = processedExpenses.filter(exp => exp.includes('skipped')).length;
        const createdExpenses = processedExpenses.filter(exp => exp.includes('created') || exp.includes('updated')).length;

        const parts = [];
        if (createdExpenses > 0) {
          parts.push(`${createdExpenses} expense(s) processed`);
        }
        if (skippedExpenses > 0) {
          parts.push(`${skippedExpenses} expense(s) skipped (automation disabled)`);
        }

        if (parts.length > 0) {
          toast.success(`✅ Costing finalized! ${parts.join(', ')}. Note: Journal entry creation failed but expenses were processed.`);
        } else {
          toast.success('✅ Costing finalized! Journal entry creation failed but costing was saved.');
        }
      }

      await loadData();
      setShowCostingModal(false);
    } catch (error) {
      console.error('❌ Error finalizing and posting costing:', error);
      toast.error(`Failed to finalize and post costing data: ${error.message}`);
    }
  };

  // Filter shipments
  const filteredShipments = shipments.filter(shipment => {
    const matchesSearch = (
      shipment.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.destination?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesStatus = statusFilter === 'all' || shipment.status === statusFilter;

    // Add costing status filtering
    const costing = getShipmentCosting(shipment.id);
    const costingStatus = costing?.costingStatus || 'not_added';
    const matchesCostingStatus = costingStatusFilter === 'all' || costingStatus === costingStatusFilter;

    // Add date range filtering
    const { start, end } = getDateRange();
    const shipmentDate = shipment.deliveryDate ?
      new Date(shipment.deliveryDate) :
      new Date(shipment.createdAt?.seconds ? shipment.createdAt.seconds * 1000 : shipment.createdAt);
    const matchesDateRange = shipmentDate >= start && shipmentDate <= end;

    return matchesSearch && matchesStatus && matchesCostingStatus && matchesDateRange;
  });

  // Helper function to check if a shipment costing has been edited after posting
  const isEditedAfterPosting = (costing) => {
    if (!costing || !costing.isPosted) return false;

    // Only check the explicit flag - this is set when costs are actually edited after posting
    return costing.editedAfterPosting === true;
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      'in-transit': 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      finalized: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {t('accounting.shipment_costing')}
          </h2>
          <p className="text-sm text-gray-500">
            {t('accounting.shipment_costing_subtitle')}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFuelImportModal(true)}
            className="btn btn-primary btn-sm flex items-center space-x-2"
          >
            <FireIcon className="w-4 h-4" />
            <span>{t('fuel.import_fuel_data')}</span>
          </button>
          <button
            onClick={() => setShowBulkFuelManager(true)}
            className="btn btn-success btn-sm flex items-center space-x-2"
            title={t('fuel.bulk_fuel_cost_manager')}
          >
            <FireIcon className="w-4 h-4" />
            <span>{t('fuel.manage_fuel_costs')}</span>
          </button>
          <button
            onClick={() => setShowFuelAnalyticsModal(true)}
            className="btn btn-secondary btn-sm flex items-center space-x-2"
          >
            <ChartBarIcon className="w-4 h-4" />
            <span>تحليلات الوقود</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-outline btn-sm flex items-center space-x-2"
          >
            <FunnelIcon className="w-4 h-4" />
            <span>{t('common.filters', 'Filters')}</span>
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-2">
              <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">
                {t('accounting.date_range', 'Date Range')}:
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'today', label: t('accounting.today', 'Today') },
                { key: 'yesterday', label: t('accounting.yesterday', 'Yesterday') },
                { key: '7days', label: t('accounting.last_7_days', 'Last 7 Days') },
                { key: 'month', label: t('accounting.this_month', 'This Month') },
                { key: '6months', label: t('accounting.last_6_months', 'Last 6 Months') },
                { key: 'year', label: t('accounting.this_year', 'This Year') },
                { key: 'custom', label: t('accounting.custom_range', 'Custom Range') }
              ].map(option => (
                <button
                  key={option.key}
                  onClick={() => setDateRange(option.key)}
                  className={`btn btn-sm ${dateRange === option.key
                    ? 'btn-primary'
                    : 'btn-outline'
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range Inputs */}
          {dateRange === 'custom' && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('accounting.start_date', 'Start Date')}
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="input input-bordered w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('accounting.end_date', 'End Date')}
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="input input-bordered w-full"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Metrics Cards */}
      {!hideMetrics && (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white rounded-xl shadow-sm border border-green-100 hover:shadow-md transition-shadow duration-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <CurrencyDollarIcon className="w-4 h-4 text-green-600" />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">
                  {t('accounting.total_revenue', 'Total Revenue')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900 mb-1">
                {formatNumber(metrics.totalRevenue)}
              </p>
              <p className="text-xs text-green-600 font-medium">ريال سعودي</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-red-100 hover:shadow-md transition-shadow duration-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="w-4 h-4 text-red-600" />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">
                  {t('accounting.total_cost', 'Total Cost')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900 mb-1">
                {formatNumber(metrics.totalCost)}
              </p>
              <p className="text-xs text-red-600 font-medium">ريال سعودي</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-shadow duration-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className={`w-8 h-8 ${metrics.totalProfit >= 0 ? 'bg-blue-100' : 'bg-red-100'} rounded-lg flex items-center justify-center`}>
                <ChartBarIcon className={`w-4 h-4 ${metrics.totalProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">
                  {t('accounting.total_profit', 'Total Profit')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-lg font-bold mb-1 ${metrics.totalProfit >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                {formatNumber(metrics.totalProfit)}
              </p>
              <p className={`text-xs font-medium ${metrics.totalProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                ريال سعودي
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-purple-100 hover:shadow-md transition-shadow duration-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="w-4 h-4 text-purple-600" />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">
                  {t('accounting.shipment_count', 'Shipments')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900 mb-1">
                {metrics.shipmentCount}
              </p>
              <p className="text-xs text-purple-600 font-medium">شحنة</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-orange-100 hover:shadow-md transition-shadow duration-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="w-4 h-4 text-orange-600" />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">
                  {t('accounting.total_fuel_cost', 'Total Fuel Cost')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900 mb-1">
                {formatNumber(metrics.totalFuelCost)}
              </p>
              <p className="text-xs text-orange-600 font-medium">ريال سعودي</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-yellow-100 hover:shadow-md transition-shadow duration-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="w-4 h-4 text-yellow-600" />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">
                  {t('accounting.total_maintenance_cost', 'Total Maintenance Cost')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900 mb-1">
                {formatNumber(metrics.totalMaintenanceCost)}
              </p>
              <p className="text-xs text-yellow-600 font-medium">ريال سعودي</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-indigo-100 hover:shadow-md transition-shadow duration-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">
                  {t('accounting.total_commission', 'Total Commission')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900 mb-1">
                {formatNumber(metrics.totalCommission)}
              </p>
              <p className="text-xs text-indigo-600 font-medium">ريال سعودي</p>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Explanation */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="card-body">
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">
                {t('accounting.metrics_explanation_title', 'About These Metrics')}
              </p>
              <p>
                {t('accounting.metrics_explanation',
                  'These metrics show revenue and costs from shipments including fuel, maintenance, stops, and commission. This does not include other business expenses like salaries, rent, or office costs - those will be shown in the reporting section.'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="card">
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder={t('accounting.search_shipments')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10"
                  />
                </div>
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="select"
                >
                  <option value="all">{t('common.all_statuses', 'All Statuses')}</option>
                  <option value="pending">{t('common.pending', 'Pending')}</option>
                  <option value="in_transit">{t('shipments.in_transit', 'In Transit')}</option>
                  <option value="delivered">{t('shipments.delivered', 'Delivered')}</option>
                  <option value="completed">{t('accounting.completed')}</option>
                  <option value="canceled">{t('shipments.cancelled', 'Canceled')}</option>
                </select>
              </div>
              <div>
                <select
                  value={costingStatusFilter}
                  onChange={(e) => setCostingStatusFilter(e.target.value)}
                  className="select"
                >
                  <option value="all">{t('accounting.all_costing_statuses')}</option>
                  <option value="not_added">{t('accounting.costing_not_added')}</option>
                  <option value="partial">{t('accounting.costing_partial')}</option>
                  <option value="completed">{t('accounting.costing_completed')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipments Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full table table-fixed">
              <thead className="table-header">
                <tr>
                  <th className="table-head w-28 text-xs">{t('shipments.tracking_number')}</th>
                  <th className="table-head w-24 text-xs">{t('common.client', 'Client')}</th>
                  <th className="table-head w-28 text-xs">{t('shipments.driver')} & {t('shipments.vehicle')}</th>
                  <th className="table-head w-24 text-xs">{t('shipments.route')}</th>
                  <th className="table-head w-20 text-xs">{t('shipments.status')}</th>
                  <th className="table-head w-24 text-xs">{t('accounting.costing_status')}</th>
                  <th className="table-head w-20 text-xs">{t('accounting.revenue')}</th>
                  <th className="table-head w-20 text-xs">{t('accounting.total_cost')}</th>
                  <th className="table-head w-24 text-xs">{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredShipments.map((shipment) => {
                  const costing = getShipmentCosting(shipment.id);
                  const revenue = calculateTotalShipmentPrice(shipment);

                  return (
                    <tr key={shipment.id} className={`table-row cursor-pointer hover:bg-gray-50 ${!costing || isEmptyCosting(costing)
                      ? 'bg-yellow-50 border-l-4 border-l-yellow-400'
                      : isEditedAfterPosting(costing)
                        ? 'bg-orange-50 border-l-4 border-l-orange-400 animate-pulse'
                        : ''
                      }`} onClick={() => openCostingModal(shipment)}>
                      <td className="table-cell w-28 text-xs p-2">
                        <div className="font-medium">{shipment.trackingNumber}</div>
                        {(!costing || isEmptyCosting(costing)) && (
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                            {t('accounting.costing_not_opened')}
                          </span>
                        )}
                        {isEditedAfterPosting(costing) && (
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-orange-100 text-orange-800 animate-pulse">
                            ⚠️ {t('accounting.needs_re_finalization')}
                          </span>
                        )}
                      </td>
                      <td className="table-cell w-24 text-xs p-2">
                        <div className="font-medium truncate">{shipment.clientName}</div>
                      </td>
                      <td className="table-cell w-28 text-xs p-2">
                        <div className="font-medium truncate">
                          {shipment.driverName || t('common.not_assigned', 'Not Assigned')}
                          {shipment.isExternalDriver && shipment.driverName && (
                            <span className="ml-1 text-xs text-blue-600 font-normal">(External)</span>
                          )}
                        </div>
                        <div className="text-gray-500 text-xs truncate">{shipment.vehiclePlate || t('common.not_assigned', 'Not Assigned')}</div>
                      </td>
                      <td className="table-cell w-24 text-xs p-2">
                        <div className="truncate">{shipment.origin} → {shipment.destination}</div>
                      </td>
                      <td className="table-cell w-20 text-xs p-2">
                        <span className={`badge text-xs ${getStatusBadgeColor(shipment.status)}`}>
                          {t(`shipments.${shipment.status}`, shipment.status)}
                        </span>
                      </td>
                      <td className="table-cell w-24 text-xs p-2">
                        <span className={`badge text-xs ${getCostingStatusBadge(costing?.costingStatus || 'not_added')}`}>
                          {getCostingStatusText(costing?.costingStatus || 'not_added')}
                        </span>
                      </td>
                      <td className="table-cell w-20 text-xs p-2">
                        <div className="font-medium text-xs">
                          SAR {revenue.toLocaleString('en-US')}
                        </div>
                      </td>
                      <td className="table-cell w-20 text-xs p-2">
                        <div className="text-xs">
                          SAR <CostCell 
                            costing={costing} 
                            shipmentId={shipment.id} 
                            companyId={currentCompany?.id} 
                          />
                        </div>
                      </td>
                      <td className="table-cell w-24 text-xs p-1">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openCostingModal(shipment);
                            }}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title={t('accounting.manage_costing')}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>

                          {/* Show Finalize & Post for non-posted costing */}
                          {costing && !costing.isPosted && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFinalizeAndPost(shipment);
                              }}
                              className="p-1 text-green-600 hover:text-green-800"
                              title={t('accounting.finalize_post')}
                            >
                              <ArrowUpTrayIcon className="w-4 h-4" />
                            </button>
                          )}

                          {/* Show Re-finalize for posted but edited costing */}
                          {costing?.isPosted && isEditedAfterPosting(costing) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFinalizeAndPost(shipment);
                              }}
                              className="p-1 text-orange-600 hover:text-orange-800 animate-pulse"
                              title={t('accounting.re_finalize_post')}
                            >
                              <ArrowPathIcon className="w-4 h-4" />
                            </button>
                          )}

                          {/* Show checkmark for posted and not edited costing */}
                          {costing?.isPosted && !isEditedAfterPosting(costing) && (
                            <CheckIcon className="w-4 h-4 text-green-600" title={t('accounting.posted')} />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Costing Modal */}
      {showCostingModal && selectedShipment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 max-w-4xl shadow-lg rounded-lg bg-white max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-5 pb-4 border-b flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {t('accounting.manage_costing_for')} {selectedShipment.trackingNumber}
                </h3>
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-gray-500">
                    {selectedShipment.origin} → {selectedShipment.destination} • {selectedShipment.clientName}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    {/* Date and Time */}
                    <div className="flex items-center gap-1">
                      <CalendarDaysIcon className="w-4 h-4" />
                      <span>{formatShipmentDate(selectedShipment)} {formatShipmentTime(selectedShipment)}</span>
                    </div>

                    {/* Driver */}
                    {selectedShipment.driverName && (
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{t('shipments.driver')}:</span>
                        <span>{selectedShipment.driverName}</span>
                      </div>
                    )}

                    {/* Vehicle */}
                    {selectedShipment.vehiclePlate && (
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{t('shipments.vehicle')}:</span>
                        <span>{selectedShipment.vehiclePlate}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Warning for posted costing */}
                {originalPostedData && (
                  <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center">
                      <span className="text-orange-600 mr-2">⚠️</span>
                      <div className="text-sm">
                        <p className="text-orange-800 font-medium">
                          {t('accounting.costing_edited_not_refinalized')}
                        </p>
                        <p className="text-orange-700">
                          {t('accounting.expenses_not_updated')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowCostingModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="space-y-4 overflow-y-auto px-2 flex-1">
              {/* Simplified Fuel Cost Section */}
              <SimplifiedFuelCostSection
                companyId={currentCompany?.id}
                shipmentId={selectedShipment?.id}
                onFuelCostUpdate={() => {
                  // Reload the costing data when fuel costs are updated
                  loadData();
                }}
                vatPreferences={vatPreferences}
                onVATPreferenceUpdate={updateVATPreference}
                validationErrors={validationErrors}
                onValidationErrorUpdate={(field, error) => {
                  setValidationErrors(prev => ({ ...prev, [field]: error }));
                }}
              />

              {/* Basic Costs Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-2 h-6 bg-blue-500 rounded mr-3"></span>
                  {t('accounting.basic_costs')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Maintenance Cost Section */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('accounting.maintenance_cost')} (SAR)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={costingData.maintenance || ''}
                      onChange={(e) => {
                        // Convert Arabic numerals to English numerals
                        const rawValue = ensureEnglishNumerals(e.target.value);
                        const numericValue = parseFloat(rawValue) || 0;
                        
                        // Calculate VAT breakdown with validation
                        const vatBreakdown = calculateMaintenanceVAT(numericValue, costingData.maintenanceIncludesVAT);
                        
                        // Update validation errors
                        setValidationErrors(prev => ({
                          ...prev,
                          maintenance: vatBreakdown.isValid ? null : vatBreakdown.error
                        }));
                        
                        // Only update state if calculation is valid or amount is zero
                        if (vatBreakdown.isValid || numericValue === 0) {
                          const newData = { 
                            ...costingData, 
                            maintenance: numericValue,
                            maintenanceBaseAmount: vatBreakdown.baseAmount,
                            maintenanceVATAmount: vatBreakdown.vatAmount
                          };
                          const newStatus = updateCostingStatus(costingData, newData);
                          setCostingData(prev => ({ 
                            ...prev, 
                            maintenance: numericValue, 
                            maintenanceBaseAmount: vatBreakdown.baseAmount,
                            maintenanceVATAmount: vatBreakdown.vatAmount,
                            costingStatus: newStatus 
                          }));

                          // Check if edited after posting
                          const wasEdited = checkIfEditedAfterPosting(newData);
                          setEditedAfterPosting(wasEdited);
                        } else {
                          // Show error but allow user to continue typing
                          setCostingData(prev => ({ 
                            ...prev, 
                            maintenance: numericValue
                          }));
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the displayed value uses English numerals
                        const cleanValue = ensureEnglishNumerals(e.target.value);
                        if (cleanValue !== e.target.value) {
                          e.target.value = cleanValue;
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        validationErrors.maintenance ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                    
                    {/* Maintenance Validation Error */}
                    {validationErrors.maintenance && (
                      <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {validationErrors.maintenance}
                        </div>
                      </div>
                    )}
                    
                    {/* Maintenance VAT Toggle */}
                    <VATToggle
                      value={costingData.maintenanceIncludesVAT}
                      onChange={async (includesVAT) => {
                        // Update preference
                        await updateVATPreference('maintenance', includesVAT);
                        
                        // Recalculate VAT breakdown with new setting and validate
                        const maintenanceAmount = parseFloat(costingData.maintenance) || 0;
                        const vatBreakdown = calculateMaintenanceVAT(maintenanceAmount, includesVAT);
                        
                        // Update validation errors
                        setValidationErrors(prev => ({
                          ...prev,
                          maintenance: vatBreakdown.isValid ? null : vatBreakdown.error
                        }));
                        
                        setCostingData(prev => ({
                          ...prev,
                          maintenanceIncludesVAT: includesVAT,
                          maintenanceBaseAmount: vatBreakdown.baseAmount,
                          maintenanceVATAmount: vatBreakdown.vatAmount
                        }));
                      }}
                      label={t('accounting.vat_toggle.maintenance_includes_vat')}
                      costType="maintenance"
                    />
                    
                    {/* Enhanced VAT Breakdown Display */}
                    {costingData.maintenance && parseFloat(costingData.maintenance) > 0 && (
                      <div className="text-xs bg-green-50 border border-green-200 p-3 rounded-lg">
                        <div className="font-medium text-green-800 mb-2">
                          {t('accounting.vat_breakdown', 'VAT Breakdown')}
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('accounting.base_amount', 'Base Amount')}:</span>
                            <span className="font-medium">SAR {costingData.maintenanceBaseAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('accounting.vat_amount', 'VAT Amount')} (15%):</span>
                            <span className="font-medium text-orange-600">SAR {costingData.maintenanceVATAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1 font-semibold">
                            <span className="text-green-800">{t('accounting.total_amount', 'Total Amount')}:</span>
                            <span className="text-green-800">SAR {(costingData.maintenanceBaseAmount + costingData.maintenanceVATAmount).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Commission Section (VAT-exempt) */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('accounting.commission')} (SAR)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={costingData.commission || ''}
                      onChange={(e) => {
                        // Convert Arabic numerals to English numerals
                        const rawValue = ensureEnglishNumerals(e.target.value);
                        const numericValue = parseFloat(rawValue) || 0;
                        
                        // Validate commission amount
                        let commissionError = null;
                        const parsedValue = parseFloat(rawValue);
                        
                        if (isNaN(parsedValue) && rawValue !== '') {
                          commissionError = 'Please enter a valid number';
                        } else {
                          const finalValue = parsedValue || 0;
                          if (finalValue < 0) {
                            commissionError = 'Commission amount cannot be negative';
                          } else if (finalValue > 999999.99) {
                            commissionError = 'Commission amount exceeds maximum allowed value';
                          }
                        }
                        
                        // Update validation errors
                        setValidationErrors(prev => ({
                          ...prev,
                          commission: commissionError
                        }));
                        
                        // Only update state if validation passes or amount is zero
                        if (!commissionError || numericValue === 0) {
                          const newData = { ...costingData, commission: numericValue };
                          const newStatus = updateCostingStatus(costingData, newData);
                          setCostingData(prev => ({ ...prev, commission: numericValue, costingStatus: newStatus }));

                          // Check if edited after posting
                          const wasEdited = checkIfEditedAfterPosting(newData);
                          setEditedAfterPosting(wasEdited);
                        } else {
                          // Show error but allow user to continue typing
                          setCostingData(prev => ({ 
                            ...prev, 
                            commission: numericValue
                          }));
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the displayed value uses English numerals
                        const cleanValue = ensureEnglishNumerals(e.target.value);
                        if (cleanValue !== e.target.value) {
                          e.target.value = cleanValue;
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        validationErrors.commission ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                    
                    {/* Commission Validation Error */}
                    {validationErrors.commission && (
                      <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {validationErrors.commission}
                        </div>
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                      {t('accounting.commission_vat_exempt', 'Commission is VAT-exempt as per Saudi regulations')}
                    </div>
                  </div>
                </div>
              </div>



              {/* Notes Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-2 h-6 bg-gray-500 rounded mr-3"></span>
                  {t('accounting.notes', 'Notes')}
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('accounting.costing_notes', 'Costing Notes')}
                  </label>
                  <textarea
                    value={costingData.notes || ''}
                    onChange={(e) => setCostingData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    rows="3"
                    placeholder={t('accounting.notes_placeholder', 'Add any notes about this costing...')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('accounting.notes_help', 'Optional notes about fuel, maintenance, commission, or other cost details')}
                  </p>
                </div>
              </div>

              {/* Costing Status Section */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-2 h-6 bg-indigo-500 rounded mr-3"></span>
                  {t('accounting.costing_status')}
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('accounting.set_costing_status')} *
                    </label>
                    <select
                      value={costingData.costingStatus}
                      onChange={(e) => setCostingData(prev => ({ ...prev, costingStatus: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="not_added">{t('accounting.costing_not_added')}</option>
                      <option value="partial">{t('accounting.costing_partial')}</option>
                      <option value="completed">{t('accounting.costing_completed')}</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('accounting.costing_status_help')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Stops Section */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                    <span className="w-2 h-6 bg-orange-500 rounded mr-3"></span>
                    {t('accounting.stops')}
                  </h4>
                  <button
                    onClick={addStop}
                    className="bg-orange-500 text-white px-3 py-1 rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-2 text-sm"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>{t('accounting.add_stop')}</span>
                  </button>
                </div>
                {costingData.stops.length === 0 ? (
                  <p className="text-gray-500 text-center py-3">
                    {t('accounting.no_stops')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {costingData.stops.map((stop, index) => (
                      <div key={index} className="bg-white p-3 rounded-lg border shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-gray-700 text-sm">{t('accounting.stop')} {index + 1}</h5>
                          <button
                            onClick={() => removeStop(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {t('accounting.stop_description')}
                            </label>
                            <input
                              type="text"
                              placeholder="e.g., Fuel station, Rest area..."
                              value={stop.description}
                              onChange={(e) => updateStop(index, 'description', e.target.value)}
                              className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                validationErrors.stops[index] && validationErrors.stops[index].includes('Description') 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300'
                              }`}
                            />
                            {validationErrors.stops[index] && validationErrors.stops[index].includes('Description') && (
                              <div className="text-xs text-red-600 mt-1">
                                {validationErrors.stops[index]}
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t('common.quantity', 'Quantity')}
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={stop.quantity}
                                onChange={(e) => updateStop(index, 'quantity', parseInt(e.target.value) || 1)}
                                className={`w-full px-2 py-2 text-sm text-center border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                  validationErrors.stops[index] && validationErrors.stops[index].includes('Quantity') 
                                    ? 'border-red-300 bg-red-50' 
                                    : 'border-gray-300'
                                }`}
                              />
                              {validationErrors.stops[index] && validationErrors.stops[index].includes('Quantity') && (
                                <div className="text-xs text-red-600 mt-1">
                                  {validationErrors.stops[index]}
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t('common.amount', 'Amount')} (ريال)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={stop.amount || ''}
                                onChange={(e) => {
                                  const rawValue = ensureEnglishNumerals(e.target.value);
                                  const numericValue = parseFloat(rawValue) || 0;
                                  updateStop(index, 'amount', numericValue);
                                }}
                                onBlur={(e) => {
                                  const cleanValue = ensureEnglishNumerals(e.target.value);
                                  if (cleanValue !== e.target.value) {
                                    e.target.value = cleanValue;
                                  }
                                }}
                                className={`w-full px-2 py-2 text-sm border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                  validationErrors.stops[index] && validationErrors.stops[index].includes('Amount') 
                                    ? 'border-red-300 bg-red-50' 
                                    : 'border-gray-300'
                                }`}
                              />
                              {validationErrors.stops[index] && validationErrors.stops[index].includes('Amount') && (
                                <div className="text-xs text-red-600 mt-1">
                                  {validationErrors.stops[index]}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Other Costs Section */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                    <span className="w-2 h-6 bg-purple-500 rounded mr-3"></span>
                    {t('accounting.other_costs')}
                  </h4>
                  <button
                    onClick={addOtherCost}
                    className="bg-purple-500 text-white px-3 py-1 rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2 text-sm"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>{t('accounting.add_cost')}</span>
                  </button>
                </div>
                {costingData.otherCosts.length === 0 ? (
                  <p className="text-gray-500 text-center py-3">
                    {t('accounting.no_other_costs')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {costingData.otherCosts.map((cost, index) => (
                      <div key={index} className="bg-white p-3 rounded-lg border shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-gray-700 text-sm">{t('accounting.expense')} {index + 1}</h5>
                          <button
                            onClick={() => removeOtherCost(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {t('accounting.cost_description')}
                            </label>
                            <input
                              type="text"
                              placeholder="e.g., Tolls, Parking, Insurance..."
                              value={cost.description}
                              onChange={(e) => updateOtherCost(index, 'description', e.target.value)}
                              className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                                validationErrors.otherCosts[index] && validationErrors.otherCosts[index].includes('Description') 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300'
                              }`}
                            />
                            {validationErrors.otherCosts[index] && validationErrors.otherCosts[index].includes('Description') && (
                              <div className="text-xs text-red-600 mt-1">
                                {validationErrors.otherCosts[index]}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {t('common.amount', 'Amount')} (ريال)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={cost.amount || ''}
                              onChange={(e) => {
                                const rawValue = ensureEnglishNumerals(e.target.value);
                                const numericValue = parseFloat(rawValue) || 0;
                                updateOtherCost(index, 'amount', numericValue);
                              }}
                              onBlur={(e) => {
                                const cleanValue = ensureEnglishNumerals(e.target.value);
                                if (cleanValue !== e.target.value) {
                                  e.target.value = cleanValue;
                                }
                              }}
                              className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                                validationErrors.otherCosts[index] && validationErrors.otherCosts[index].includes('Amount') 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300'
                              }`}
                            />
                            {validationErrors.otherCosts[index] && validationErrors.otherCosts[index].includes('Amount') && (
                              <div className="text-xs text-red-600 mt-1">
                                {validationErrors.otherCosts[index]}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Summary Section */}
              <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-2 h-6 bg-green-500 rounded mr-3"></span>
                  {t('accounting.cost_summary')}
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="text-center p-2 bg-white rounded-lg">
                    <div className="text-gray-600 text-xs">{t('accounting.revenue')}</div>
                    <div className="text-sm font-bold text-green-600">SAR {calculateSubtotalBeforeTax(selectedShipment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <div className="text-gray-600 text-xs">{t('accounting.total_cost')}</div>
                    <div className="text-sm font-bold text-red-600">
                      SAR <CostCell 
                        costing={{ ...costingData, fuel: parseFloat(costingData.fuel) || 0, maintenance: parseFloat(costingData.maintenance) || 0, commission: parseFloat(costingData.commission) || 0 }} 
                        shipmentId={selectedShipment?.id} 
                        companyId={currentCompany?.id} 
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t bg-gray-50 -mx-5 -mb-5 px-5 py-4 rounded-b-lg flex-shrink-0">
              {/* Re-finalization notice */}
              {editedAfterPosting && originalPostedData && (
                <div className="flex items-center text-sm text-orange-700 bg-orange-100 px-3 py-2 rounded-lg animate-pulse">
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  {t('accounting.re_finalize_to_update')}
                </div>
              )}

              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowCostingModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleSaveCosting}
                  disabled={
                    validationErrors.fuel || 
                    validationErrors.maintenance || 
                    validationErrors.commission ||
                    validationErrors.stops.some(error => error !== null) ||
                    validationErrors.otherCosts.some(error => error !== null)
                  }
                  className={`px-6 py-2 rounded-lg transition-colors font-medium ${
                    validationErrors.fuel || 
                    validationErrors.maintenance || 
                    validationErrors.commission ||
                    validationErrors.stops.some(error => error !== null) ||
                    validationErrors.otherCosts.some(error => error !== null)
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {t('common.save', 'Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Fuel Import Modal */}
      <FuelImportModal
        isOpen={showFuelImportModal}
        onClose={() => setShowFuelImportModal(false)}
        onImportComplete={(result) => {
          // Refresh data after successful import
          loadData();
          setShowFuelImportModal(false);
          toast.success(`Successfully imported ${result.importedCount} fuel transactions`);
        }}
      />

      {/* Fuel Analytics Modal */}
      <FuelAnalyticsModal
        isOpen={showFuelAnalyticsModal}
        onClose={() => setShowFuelAnalyticsModal(false)}
      />

      {/* Bulk Fuel Cost Manager Modal */}
      <BulkFuelCostManager
        isOpen={showBulkFuelManager}
        onClose={() => setShowBulkFuelManager(false)}
        companyId={currentCompany?.id}
        onUpdate={loadData}
      />
    </div>
  );
};

export default ShipmentCosting; 